import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import logo from "../../logo.png";
import "./SideBar.css";

/**
 * Modern Sidebar with Clear Role Separation
 * 100% DATA FROM AUTH API - NO DUMMY DATA
 * Completely different UI for USER vs SUPERADMIN
 */
export default function Sidebar({ sideBarVisibility }) {
  const { user, isSuperAdmin } = useAuth();
  const location = useLocation();

  if (!user) return null;

  // Regular USER routes - Simple & focused
  const userRoutes = [
    {
      path: "/",
      label: "Dashboard",
      icon: "las la-home",
      description: "Your overview",
      exact: true,
    },
    {
      path: "/my-tickets",
      label: "My Tickets",
      icon: "las la-ticket-alt",
      description: "Your tickets",
    },
    {
      path: "/add-new-ticket",
      label: "New Ticket",
      icon: "las la-plus-circle",
      description: "Create ticket",
    },
  ];

  // SUPERADMIN routes - Full control panel
  const superAdminRoutes = [
    {
      path: "/",
      label: "Dashboard",
      icon: "las la-tachometer-alt",
      // description: "System Overview",
      exact: true,
    },
    {
      path: "/tickets",
      label: "All Tickets",
      icon: "las la-list",
      // description: "Manage all tickets",
      // badge: "Admin",
    },
    {
      path: "/my-tickets",
      label: "My Tickets",
      icon: "las la-user-circle",
      // description: "Your tickets",
    },
    {
      path: "/add-new-ticket",
      label: "New Ticket",
      icon: "las la-plus-circle",
      // description: "Create ticket",
    },
  ];

  const routes = isSuperAdmin ? superAdminRoutes : userRoutes;

  const isActive = (route) => {
    if (route.exact) {
      return location.pathname === route.path;
    }
    return location.pathname.startsWith(route.path);
  };

  return (
    <div
      className={`sidebar-modern ${sideBarVisibility ? "active" : ""} ${
        isSuperAdmin ? "admin" : "user"
      }`}
    >
      {/* Sidebar Header - Different for USER vs ADMIN */}
      <div className="sidebar-header-modern">
        <div className="sidebar-logo-modern">
          <img
            src={logo}
            alt="ESGFIT Logo"
            style={{ width: "148px", height: "auto", objectFit: "contain", padding: "10px",height:"69px" }}
          />
        </div>
      </div>

      {/* Navigation Menu - Completely different for each role */}
      <nav className="sidebar-nav-modern">
        {routes.map((route) => (
          <Link
            key={route.path}
            to={route.path}
            className={`nav-link-modern ${isActive(route) ? "active" : ""}`}
          >
            <div className="nav-icon-modern">
              <i className={route.icon} />
            </div>
            <div className="nav-content-modern">
              <div className="nav-label-modern">{route.label}</div>
              <div className="nav-description-modern">{route.description}</div>
            </div>
            {route.badge && (
              <span className="nav-badge-modern">{route.badge}</span>
            )}
            {isActive(route) && <div className="active-indicator-modern" />}
          </Link>
        ))}
      </nav>

      {/* Sidebar Footer */}
      <div className="sidebar-footer-modern">
        {/* User Info in Sidebar - DATA FROM AUTH API */}
        <div className="sidebar-user-info">
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: isSuperAdmin ? "#3bac75" : "#007bff",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "600",
              fontSize: "13px",
            }}
          >
            {user?.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 2)}
          </div>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div
              style={{
                fontWeight: "600",
                fontSize: "13px",
                color: "#212529",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user?.name}
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "#6c757d",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user?.email}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Clean, minimal sidebar matching Survey Dashboard */
        .sidebar-modern {
          width: 200px;
          max-width: 200px;
          height: 100vh;
          background: #ffffff;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          position: fixed;
          left: 0;
          top: 0;
          z-index: 100;
          transition: transform 0.2s ease-linear;
        }

        .sidebar-header-modern {
          padding: 0;
          border-bottom: 1px solid #e5e7eb;
        }

        .sidebar-logo-modern {
          display: flex;
          align-items: center;
          margin-bottom: 0;
          padding: 0;
        }

        .sidebar-nav-modern {
          flex: 1;
          padding: 24px 18px;
          overflow-y: auto;
          overflow-x: hidden;
        }

        /* Custom scrollbar */
        .sidebar-nav-modern::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav-modern::-webkit-scrollbar-track {
          background: transparent;
        }

        .sidebar-nav-modern::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }

        .sidebar-nav-modern::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        .nav-link-modern {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          margin-bottom: 4px;
          border-radius: 8px;
          text-decoration: none;
          color: #4b5563;
          transition: all 0.15s ease;
          position: relative;
        }

        .nav-link-modern:hover {
          background-color: #f3f4f6;
          color: #111827;
        }

        .nav-link-modern.active {
          background-color: #3bac75;
          color: #ffffff;
        }

        .nav-icon-modern {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 24px;
          height: 24px;
        }

        .nav-icon-modern i {
          font-size: 22px !important;
          line-height: 1;
        }

        .nav-content-modern {
          flex: 1;
          min-width: 0;
        }

        .nav-label-modern {
          font-size: 14px;
          font-weight: 500;
          line-height: 1.4;
        }

        .nav-description-modern {
          display: none;
        }

        .nav-badge-modern {
          font-size: 10px;
          background: rgba(255,255,255,0.2);
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
        }

        .active-indicator-modern {
          display: none;
        }

        .sidebar-footer-modern {
          padding: 16px;
          border-top: 1px solid #e5e7eb;
          background: #ffffff;
        }

        .apps-link-modern {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          background: transparent;
          border-radius: 8px;
          text-decoration: none;
          color: #4b5563;
          transition: all 0.15s ease;
          margin-bottom: 12px;
          border: 1px solid #e5e7eb;
        }

        .apps-link-modern:hover {
          background-color: #f3f4f6;
          border-color: #3bac75;
          color: #3bac75;
        }

        .apps-link-modern i {
          font-size: 20px;
        }

        .sidebar-user-info {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
        }

        @media (max-width: 768px) {
          .sidebar-modern {
            transform: translateX(-100%);
          }

          .sidebar-modern.active {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}
